// The expedition in progress, as the client holds it (decision 0015): the server's last answer is the truth; while an
// action waits for its answer the screen shows it done (`optimistic`), and a refusal brings back the server's list.
// No rule runs here — the screen checks an action on the Forge's engine before sending it — so this module stays out
// of the Forge's bundle and can sit in the game's first chunk.
import { signal } from '@preact/signals';
import { abandonExpedition, expeditionAct, fetchExpedition, fetchExpeditionState, fetchPublicConfig, startExpedition, toast, type Expedition, type ExpeditionErrorDetails, type RtStartOptions } from '../net.js';
import { t } from '../i18n/index.js';

/** The server offers expeditions (publicConfig.expeditions). */
export const expeditionsOn = signal(false);
/** The server offers the deck in 3D (publicConfig.expedition3d). */
export const expedition3d = signal(false);
/** The server plays new expeditions as the real-time dungeon (rules 7): the entry opens the preparation screen first. */
export const expeditionRt = signal(false);
/** The preparation screen, open on a wreck (system, poi); null: closed. */
export const rtPrep = signal<{ system: string; poi: string } | null>(null);
export const expedition = signal<Expedition | null>(null);
/** The actions shown while one waits for the server's answer; null when nothing is pending. */
export const optimistic = signal<string[] | null>(null);
export const expeditionOpen = signal(false);
/** Until when the team rests after its last expedition (null: ready). */
export const restUntil = signal<number | null>(null);
async function refreshRest(): Promise<void> { const s = await fetchExpeditionState(); if (s) restUntil.value = s.restUntil; }

export const xpError = (code: string): string => t('xpErrors')[code] ?? code;
function say(code: string): void {
  const text = xpError(code);
  toast.value = { text, kind: 'err' };
  setTimeout(() => { if (toast.value?.text === text) toast.value = null; }, 3500);
}

/** On entering the game: is the feature on, and is an expedition waiting to be resumed. */
export async function loadExpeditions(): Promise<void> {
  const cfg = await fetchPublicConfig();
  expeditionsOn.value = cfg.expeditions === true;
  expedition3d.value = cfg.expedition3d === true;
  expeditionRt.value = cfg.expeditionRt === true;
  if (!expeditionsOn.value) return;
  const s = await fetchExpeditionState();
  expedition.value = s?.active ?? null;
  restUntil.value = s?.restUntil ?? null;
}

/** Starts an expedition on a wreck and opens its screen; one already under way is opened instead. */
export async function beginExpedition(system: string, poi: string): Promise<boolean> {
  const r = await startExpedition(system, poi);
  if (r.ok) { expedition.value = r.exp; expeditionOpen.value = true; return true; }
  if (r.error === 'resting') await refreshRest();
  if (r.error === 'already_active') {
    const cur = await fetchExpedition();
    if (cur) { expedition.value = cur; expeditionOpen.value = true; }
  }
  say(r.error);
  return false;
}

/** Rules 7: leaves with the loadout of the preparation screen. Opens the dungeon on success; the refusal (and what it says
 *  besides its code: what the Colony lacks, what cannot embark) comes back to the screen, which says it. */
export async function startRt(system: string, poi: string, opts: RtStartOptions): Promise<{ ok: true } | { ok: false; error: string; details?: ExpeditionErrorDetails }> {
  const r = await startExpedition(system, poi, undefined, opts);
  if (r.ok) { expedition.value = r.exp; rtPrep.value = null; expeditionOpen.value = true; return { ok: true }; }
  if (r.error === 'resting') await refreshRest();
  if (r.error === 'already_active') {
    const cur = await fetchExpedition();
    if (cur) { expedition.value = cur; rtPrep.value = null; expeditionOpen.value = true; }
  }
  return { ok: false, error: r.error, ...(r.details ? { details: r.details } : {}) };
}

/** After a rules-7 game: the Colony's rest and troops may have changed. */
export const refreshAfterRt = refreshRest;

/** Sends an action the engine allows. The answer replaces the game; a refusal brings the server's own list back. */
export async function sendAction(id: string): Promise<boolean> {
  const base = expedition.value;
  if (!base || optimistic.value) return false;
  optimistic.value = [...base.done, id];
  const r = await expeditionAct(id);
  if (r.ok) { expedition.value = r.exp; if (r.exp.restUntil !== undefined) restUntil.value = r.exp.restUntil; }
  else {
    // The server is the truth: ask it where the game stands; unreachable (or nothing to say), its last answer stands.
    const back = r.error === 'offline' ? null : await fetchExpedition();
    expedition.value = back ?? base;
    say(r.error);
  }
  optimistic.value = null;
  return r.ok;
}

export async function abandon(): Promise<boolean> {
  const ok = await abandonExpedition();
  if (ok) { expedition.value = null; expeditionOpen.value = false; optimistic.value = null; void refreshRest(); } else say('offline');
  return ok;
}
